import { useEffect, useRef } from "react";
import { ArrowRight, Sparkles, Star, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useRole } from "@/contexts/RoleContext";
import { useLanguage } from "@/contexts/LanguageContext";

export function CTASection() {
  const { isStudent } = useRole();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("visible");
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-28 relative overflow-hidden">
      {/* Vibrant animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[hsl(237,52%,20%)] via-[hsl(237,52%,31%)] to-[hsl(237,52%,18%)] animated-gradient" />

      {/* Decorative elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-10 left-[10%] w-72 h-72 bg-secondary/15 rounded-full blur-[100px] animate-pulse-ring" />
        <div className="absolute bottom-10 right-[10%] w-80 h-80 bg-[hsl(260,60%,50%)]/12 rounded-full blur-[100px] animate-pulse-ring" style={{ animationDelay: "1.5s" }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[hsl(35,95%,55%)]/8 rounded-full blur-[80px]" />

        {/* Grid pattern */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.04]">
          <defs>
            <pattern id="cta-grid" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#cta-grid)" />
        </svg>

        {/* Floating decorative icons */}
        <div className="hidden lg:block">
          <div className="absolute top-16 left-[15%] animate-float-slow">
            <div className="w-10 h-10 rounded-xl bg-secondary/20 backdrop-blur-sm flex items-center justify-center">
              <Star className="w-5 h-5 text-secondary" />
            </div>
          </div>
          <div className="absolute bottom-16 right-[15%] animate-float-delayed">
            <div className="w-10 h-10 rounded-xl bg-[hsl(35,95%,55%)]/20 backdrop-blur-sm flex items-center justify-center">
              <Zap className="w-5 h-5 text-[hsl(35,95%,55%)]" />
            </div>
          </div>
        </div>
      </div>

      <div ref={ref} className="container mx-auto px-4 relative z-10 reveal">
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 border border-white/15 text-primary-foreground text-sm font-medium mb-8 backdrop-blur-md shimmer">
            <Sparkles className="w-3.5 h-3.5 text-[hsl(35,95%,55%)]" />
            {isStudent ? t("cta.badge.student") : t("cta.badge.coach")}
          </div>

          <h2 className="font-sans text-4xl sm:text-5xl lg:text-6xl text-primary-foreground mb-6 leading-tight">
            {isStudent
              ? t("cta.headline.student")
              : t("cta.headline.coach")
            }
          </h2>
          <p className="text-lg text-primary-foreground/65 mb-12 max-w-lg mx-auto leading-relaxed">
            {isStudent ? t("cta.sub.student") : t("cta.sub.coach")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              size="lg"
              onClick={() => navigate("/register")}
              className="rounded-full px-12 py-7 text-base font-semibold bg-white text-[hsl(237,52%,31%)] hover:bg-white/90 shadow-xl shadow-black/20 hover:shadow-2xl transition-all duration-300 hover:-translate-y-0.5 active:brightness-95"
            >
              {t("nav.signUp")}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate("/login")}
              className="rounded-full px-12 py-7 text-base font-semibold border-white/30 text-primary-foreground hover:bg-white/10 hover:text-primary-foreground"
            >
              {t("nav.login")}
            </Button>
          </div>

          {/* Trust line */}
          <p className="mt-8 text-xs text-primary-foreground/40">
            {t("cta.trust")}
          </p>
        </div>
      </div>
    </section>
  );
}
